// TodayCommandView — the "Health Client Scheduling Command Center" default
// view for the Health Clients calendar. Instead of a month grid, it answers,
// per appointment: who, when, what type, is the client READY, what workflow
// are they in, and the fast actions to work them.
//
// HONESTY CONTRACT: readiness is "X of N real items on file" from
// /api/calendar/appointment-readiness (real columns only). The workflow strip
// reuses /api/contacts/workflow-status. There are NO invented percentages,
// enrollment probabilities, or revenue projections here — where data is
// absent, the card says so. Actions route through the app's existing global
// events (dial / open client / SMS composer) so nothing new fires blindly.

import React from 'react';
import { authedFetch } from '../lib/authedFetch';

const STATUS_PILL = {
  scheduled:   { label: 'Scheduled',  cls: 'bg-blue-100 text-blue-800' },
  closed_won:  { label: 'Won',        cls: 'bg-emerald-100 text-emerald-800' },
  closed_lost: { label: 'Lost',       cls: 'bg-slate-200 text-slate-700' },
  no_show:     { label: 'No-show',    cls: 'bg-amber-100 text-amber-800' },
  cancelled:   { label: 'Cancelled',  cls: 'bg-slate-200 text-slate-600' },
  rescheduled: { label: 'Rescheduled',cls: 'bg-violet-100 text-violet-800' },
};

const READY_ICON = { present: '✓', missing: '•', unknown: '·' };
const READY_CLS = { present: 'text-emerald-600', missing: 'text-amber-600', unknown: 'text-slate-300' };

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtTime(t) {
  if (!t || !/^\d{2}:\d{2}/.test(t)) return '—';
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
}
function dispatch(name, detail) { try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* noop */ } }

// ── Per-appointment readiness + workflow, lazily loaded ───────────────
function ReadinessBlock({ clientId }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    let cancel = false;
    if (!clientId) { setState({ loading: false, data: null, error: 'unlinked' }); return; }
    setState({ loading: true, data: null, error: null });
    authedFetch(`/api/calendar/appointment-readiness?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => { if (!cancel) setState({ loading: false, data: r.ok ? r.data : null, error: r.ok ? null : (r.error || 'load_failed') }); });
    return () => { cancel = true; };
  }, [clientId]);

  if (!clientId) {
    return <div className="text-xs text-slate-400 italic">Link this appointment to a client record to see readiness.</div>;
  }
  if (state.loading) return <div className="text-xs text-slate-400">Checking readiness…</div>;
  if (!state.data) return <div className="text-xs text-slate-400">Readiness unavailable{state.error ? ` (${state.error})` : ''}.</div>;

  const { present, countable, items } = state.data;
  const pct = countable > 0 ? present / countable : 0;
  const bar = pct >= 0.8 ? 'bg-emerald-500' : pct >= 0.5 ? 'bg-amber-500' : 'bg-rose-500';
  const label = pct >= 0.8 ? 'text-emerald-700' : pct >= 0.5 ? 'text-amber-700' : 'text-rose-700';

  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 text-left">
        <span className={`text-xs font-bold ${label}`}>Ready {present}/{countable}</span>
        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${bar} rounded-full transition-all`} style={{ width: `${Math.round(pct * 100)}%` }}/>
        </div>
        <span className="text-xs text-slate-400">{open ? 'Hide' : 'Details'}</span>
      </button>
      {open && (
        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {items.map((it) => (
            <li key={it.key} className="flex items-start gap-1.5 text-xs">
              <span className={`${READY_CLS[it.status]} font-bold leading-5`}>{READY_ICON[it.status]}</span>
              <span className="text-slate-600"><span className="font-medium text-slate-800">{it.label}:</span> {it.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WorkflowStrip({ clientId }) {
  const [wf, setWf] = React.useState(undefined);
  React.useEffect(() => {
    let cancel = false;
    if (!clientId) { setWf(null); return; }
    setWf(undefined);
    authedFetch(`/api/contacts/workflow-status?target_type=client&target_id=${encodeURIComponent(clientId)}`)
      .then((r) => { if (!cancel) setWf(r.ok && Array.isArray(r.data?.enrollments) ? r.data.enrollments : []); });
    return () => { cancel = true; };
  }, [clientId]);

  if (!clientId || wf === undefined) return null;
  const active = wf.find((e) => e.status === 'active') || wf[0];
  if (!active) return <div className="text-xs text-slate-400 italic">Not in any workflow.</div>;
  const waiting = active.status_label === 'Waiting';
  return (
    <div className={`text-xs rounded-lg px-2.5 py-1.5 ${waiting ? 'bg-amber-50 text-amber-900' : 'bg-blue-50 text-blue-900'}`}>
      <span className="font-semibold">{active.workflow}</span> · {active.status_label}
      {active.current_step ? <span className="text-slate-500"> · {active.current_step}</span> : null}
      {active.why_waiting ? <div className="text-slate-500 mt-0.5">{active.why_waiting}</div> : null}
    </div>
  );
}

function ActionBtn({ onClick, children, tone = 'default', disabled }) {
  const tones = {
    default: 'border-slate-200 text-slate-700 hover:bg-slate-50',
    primary: 'border-emerald-200 text-emerald-700 hover:bg-emerald-50',
    warn: 'border-amber-200 text-amber-700 hover:bg-amber-50',
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`px-2.5 py-1 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed ${tones[tone]}`}>
      {children}
    </button>
  );
}

function CommandCard({ appt, agentName, openClientProfile, onEdit, onSetStatus }) {
  const clientId = appt.clientId && /^[0-9a-f-]{36}$/i.test(appt.clientId) ? appt.clientId : null;
  const pill = STATUS_PILL[appt.status] || STATUS_PILL.scheduled;
  const openClient = () => {
    if (clientId && openClientProfile) openClientProfile(clientId);
    else if (clientId) dispatch('crm-open-client', { client_id: clientId });
  };
  // Start appointment — the Calendar → Phone handoff in one tap: open the
  // client workspace (the pre-call brief) AND place the call. No new plumbing
  // and no status write (the status enum has no in-progress state) — it just
  // reuses the app's existing crm-open-client + crm-dial-request events so the
  // agent lands on the client with the call already ringing.
  const startAppointment = () => {
    if (clientId && openClientProfile) openClientProfile(clientId);
    else if (clientId) dispatch('crm-open-client', { client_id: clientId });
    if (appt.prospectPhone) dispatch('crm-dial-request', { toNumber: appt.prospectPhone, contactName: appt.prospectName, leadId: appt.leadId });
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <div className="text-center min-w-[64px]">
          <div className="text-lg font-bold text-slate-900 leading-tight">{fmtTime(appt.appointmentTime)}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-bold text-slate-900 truncate">{appt.prospectName || 'Unnamed'}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${pill.cls}`}>{pill.label}</span>
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            {appt.type || 'Appointment'}
            {appt.leadSource ? ` · ${appt.leadSource}` : ''}
            {agentName ? ` · ${agentName}` : ''}
          </div>
        </div>
      </div>

      <ReadinessBlock clientId={clientId}/>
      <WorkflowStrip clientId={clientId}/>

      <div className="flex flex-wrap gap-1.5 pt-1">
        {clientId && appt.prospectPhone
          ? <ActionBtn onClick={startAppointment} tone="primary">▶ Start appointment</ActionBtn>
          : <ActionBtn onClick={openClient} disabled={!clientId} tone="primary">Open client</ActionBtn>}
        <ActionBtn onClick={() => dispatch('crm-dial-request', { toNumber: appt.prospectPhone, contactName: appt.prospectName, leadId: appt.leadId })} disabled={!appt.prospectPhone}>Start call</ActionBtn>
        <ActionBtn onClick={() => dispatch('inbox:compose', { phone: appt.prospectPhone, name: appt.prospectName })} disabled={!appt.prospectPhone}>Text</ActionBtn>
        <ActionBtn onClick={() => onEdit(appt.id)}>Reschedule</ActionBtn>
        <ActionBtn onClick={() => onSetStatus(appt.id, 'closed_won')} tone="primary">Mark won</ActionBtn>
        <ActionBtn onClick={() => onSetStatus(appt.id, 'no_show')} tone="warn">No-show</ActionBtn>
      </div>
    </div>
  );
}

function OpsMetrics() {
  const [ops, setOps] = React.useState(null);
  React.useEffect(() => {
    let cancel = false;
    authedFetch('/api/calendar/ops-summary').then((r) => { if (!cancel) setOps(r.ok ? r.data : null); });
    return () => { cancel = true; };
  }, []);
  if (!ops) return null;
  const num = (v) => (v == null ? '—' : v);
  const lapse = ops.lapse_risk || {};
  const lapseTotal = (lapse.lapsed == null && lapse.renewal_overdue == null) ? null : (lapse.lapsed || 0) + (lapse.renewal_overdue || 0);
  const tile = (label, value, cls, sub) => (
    <div className={`rounded-xl border-2 px-3 py-2 ${cls}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{num(value)}</div>
      {sub && <div className="text-[10.5px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {tile('Renewals this week', ops.renewals_this_week, 'bg-violet-50 border-violet-200 text-violet-900', ops.renewals_next_30 != null ? `${ops.renewals_next_30} in 30 days` : null)}
      {tile('Documents outstanding', ops.documents_outstanding, 'bg-orange-50 border-orange-200 text-orange-900', 'clients with a requested/rejected doc')}
      {tile('Lapse risk', lapseTotal, 'bg-rose-50 border-rose-200 text-rose-900', lapse.renewal_overdue != null ? `${lapse.renewal_overdue} renewal overdue · ${num(lapse.lapsed)} lapsed` : null)}
    </div>
  );
}

export default function TodayCommandView({ calendar = [], agentChoices = [], openClientProfile, onEdit, onSetStatus }) {
  const tk = todayKey();
  const agentName = (pn) => agentChoices.find(([p]) => p === pn)?.[1] || null;

  const parseTime = (a) => (a.appointmentTime && /^\d{2}:\d{2}/.test(a.appointmentTime) ? a.appointmentTime : '99:99');
  const todays = calendar
    .filter((a) => a.appointmentDate === tk)
    .sort((a, b) => parseTime(a).localeCompare(parseTime(b)));

  // If nothing today, surface the next day that HAS appointments.
  const upcoming = calendar
    .filter((a) => a.appointmentDate && a.appointmentDate > tk && a.status === 'scheduled')
    .sort((a, b) => (a.appointmentDate + parseTime(a)).localeCompare(b.appointmentDate + parseTime(b)));
  const nextDayKey = upcoming[0]?.appointmentDate || null;
  const nextDay = nextDayKey ? upcoming.filter((a) => a.appointmentDate === nextDayKey) : [];

  const metric = (label, value, cls) => (
    <div className={`rounded-xl border-2 px-3 py-2 ${cls}`}>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
    </div>
  );
  const cnt = {
    total: todays.length,
    scheduled: todays.filter((a) => a.status === 'scheduled').length,
    won: todays.filter((a) => a.status === 'closed_won').length,
    noshow: todays.filter((a) => a.status === 'no_show').length,
  };

  const cardProps = { agentName: null, openClientProfile, onEdit, onSetStatus };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {metric('Today', cnt.total, 'bg-slate-50 border-slate-200 text-slate-800')}
        {metric('Scheduled', cnt.scheduled, 'bg-blue-50 border-blue-200 text-blue-900')}
        {metric('Won today', cnt.won, 'bg-emerald-50 border-emerald-200 text-emerald-900')}
        {metric('No-show today', cnt.noshow, 'bg-amber-50 border-amber-200 text-amber-900')}
      </div>

      {/* Real operations metrics from the health book (renewals / documents / lapse) */}
      <OpsMetrics/>

      {todays.length > 0 ? (
        <div className="space-y-3">
          <div className="text-sm font-semibold text-slate-700">Today's schedule</div>
          {todays.map((a) => <CommandCard key={a.id} appt={a} {...cardProps} agentName={agentName(a.agentProdNum)}/>)}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
          <div className="text-slate-500">No appointments today.</div>
          {nextDay.length > 0 && (
            <div className="mt-4 text-left">
              <div className="text-sm font-semibold text-slate-700 mb-2">
                Next up — {new Date(nextDayKey + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>
              <div className="space-y-3">
                {nextDay.map((a) => <CommandCard key={a.id} appt={a} {...cardProps} agentName={agentName(a.agentProdNum)}/>)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
